import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  ArrowUpDown,
  Bot,
  ExternalLink,
  GitCommit,
  MessageSquare,
  Sparkles,
  Terminal,
  UserCheck,
  UserRound,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { canonicalStateChip } from "@/components/finding-badges";
import { formatRelativeTime } from "@/lib/freshness";
import { cn } from "@/lib/utils";
import type { ProjectActivityEntry } from "./activity-feed-types";

interface ActivityFeedEntryProps {
  entry: ProjectActivityEntry;
  projectId: string;
}

export function ActivityFeedEntry({
  entry,
  projectId,
}: ActivityFeedEntryProps) {
  const actor =
    entry.actorName ??
    (entry.actorKind === "run"
      ? "Walkthrough Run"
      : entry.actorKind === "cli"
        ? "CLI sync"
        : entry.actorKind === "tracker"
          ? "Tracker Sync"
          : "Teammate");

  const relativeTime = formatRelativeTime(entry.createdAt);
  const fullTime = new Date(entry.createdAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <div className="group relative flex items-start gap-3 py-3 transition-colors">
      {/* Icon node */}
      <div className="mt-0.5 shrink-0">
        <EntryIcon kind={entry.kind} actorKind={entry.actorKind} />
      </div>

      {/* Main content body */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground leading-snug">
            <span className="font-semibold text-foreground">{actor}</span>
            <ActorBadge actorKind={entry.actorKind} />
            <EntryActionDescription entry={entry} />
          </div>
          <time
            dateTime={entry.createdAt}
            title={fullTime}
            className="text-2xs text-muted-foreground shrink-0 tabular-nums cursor-default pt-0.5"
          >
            {relativeTime}
          </time>
        </div>

        {/* Associated finding link */}
        {entry.canonicalFindingId && (
          <div className="flex items-center gap-1.5 text-xs">
            <Link
              to={`/projects/${projectId}/findings/${entry.canonicalFindingId}`}
              className="font-medium text-foreground hover:underline line-clamp-1 flex-1 min-w-0"
            >
              <span className="text-muted-foreground mr-1">#</span>
              {entry.canonicalTitle ??
                `Finding ${entry.canonicalFindingId.slice(0, 8)}`}
            </Link>
            {entry.walkthroughId && (
              <Link
                to={`/projects/${projectId}/walkthroughs/${entry.walkthroughId}`}
                className="text-2xs text-muted-foreground hover:text-foreground hover:underline shrink-0"
              >
                run details →
              </Link>
            )}
          </div>
        )}

        {/* Comment body */}
        {entry.body && (
          <div className="text-xs text-muted-foreground border-l-2 border-border/80 pl-2.5 py-0.5 whitespace-pre-wrap bg-muted/20 rounded-r">
            &ldquo;{entry.body}&rdquo;
          </div>
        )}

        {/* Reason / change note (suppressed for state transitions since the state chip already renders the reason) */}
        {entry.reason && !entry.body && entry.kind !== "state" && (
          <p className="text-2xs text-muted-foreground font-mono bg-muted/40 px-2 py-0.5 rounded w-fit max-w-full truncate">
            {entry.reason}
          </p>
        )}
      </div>
    </div>
  );
}

function EntryIcon({ kind, actorKind }: { kind: string; actorKind: string }) {
  if (actorKind === "run") {
    return (
      <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
        <Sparkles className="size-3.5" />
      </span>
    );
  }
  if (actorKind === "cli") {
    return (
      <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
        <Terminal className="size-3.5" />
      </span>
    );
  }
  if (actorKind === "tracker") {
    return (
      <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
        <ExternalLink className="size-3.5" />
      </span>
    );
  }

  switch (kind) {
    case "assigned":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <UserCheck className="size-3.5" />
        </span>
      );
    case "state":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <GitCommit className="size-3.5" />
        </span>
      );
    case "comment":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <MessageSquare className="size-3.5" />
        </span>
      );
    case "linked":
    case "unlinked":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <ExternalLink className="size-3.5" />
        </span>
      );
    case "priority_override":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <ArrowUpDown className="size-3.5" />
        </span>
      );
    default:
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground border border-border/60">
          <Activity className="size-3.5" />
        </span>
      );
  }
}

function ActorBadge({ actorKind }: { actorKind: string }) {
  if (actorKind === "cli") {
    return (
      <Chip tone="neutral" className="text-2xs py-0 px-1">
        CLI
      </Chip>
    );
  }
  return null;
}

function EntryActionDescription({ entry }: { entry: ProjectActivityEntry }) {
  switch (entry.kind) {
    case "assigned": {
      if (entry.actorKind === "cli") {
        const tool = entry.payload?.tool as string | undefined;
        return (
          <span className="text-muted-foreground">
            claimed work request{tool ? ` for ${tool}` : ""}
          </span>
        );
      }
      const toName =
        (entry.payload?.assigneeName as string | undefined) ??
        (entry.payload?.to ? "teammate" : null);

      if (!toName) {
        return (
          <span className="text-muted-foreground">unassigned this finding</span>
        );
      }
      return (
        <span className="text-muted-foreground">
          assigned to{" "}
          <span className="font-medium text-foreground">{toName}</span>
        </span>
      );
    }
    case "state": {
      if (!entry.fromState) {
        return (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            marked as
            {renderStateChip(entry.toState, entry.reason)}
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          transitioned
          {renderStateChip(entry.fromState)}
          <ArrowRight className="size-2.5 shrink-0" />
          {renderStateChip(entry.toState, entry.reason)}
        </span>
      );
    }
    case "comment":
      return <span className="text-muted-foreground">added a note</span>;
    case "outbound_comment":
      return (
        <span className="text-muted-foreground">
          synced note to issue tracker
        </span>
      );
    case "linked": {
      const key =
        (entry.payload?.displayKey as string | undefined) ??
        (entry.payload?.key as string | undefined) ??
        "issue";
      return (
        <span className="text-muted-foreground">
          linked <span className="font-mono text-foreground">{key}</span>
        </span>
      );
    }
    case "unlinked":
      return (
        <span className="text-muted-foreground">unlinked external issue</span>
      );
    case "external_state": {
      const displayKey = entry.payload?.displayKey as string | undefined;
      const rawState =
        (entry.payload?.state as string | undefined) ??
        (entry.payload?.to as string | undefined) ??
        "updated";
      const stateLabel =
        rawState === "in_progress"
          ? "In progress"
          : rawState === "done"
            ? "Completed"
            : rawState === "wont_do"
              ? "Not planned"
              : rawState === "open"
                ? "Open"
                : rawState;
      return (
        <span className="text-muted-foreground">
          {displayKey ? (
            <>
              synced{" "}
              <span className="font-mono text-foreground">{displayKey}</span>:{" "}
            </>
          ) : (
            "sync: state changed to "
          )}
          <span className="font-medium text-foreground">{stateLabel}</span>
        </span>
      );
    }
    case "priority_override": {
      const p =
        (entry.payload?.priority as string | undefined) ??
        (entry.payload?.to as string | undefined);
      return (
        <span className="text-muted-foreground">
          overrode priority
          {p ? (
            <>
              {" "}
              to{" "}
              <span className="font-mono font-semibold text-foreground">
                {p}
              </span>
            </>
          ) : (
            ""
          )}
        </span>
      );
    }
    default:
      return <span className="text-muted-foreground">updated finding</span>;
  }
}

function renderStateChip(
  state: string | null | undefined,
  reason?: string | null,
) {
  if (!state) return null;
  const chip = canonicalStateChip(state, reason);
  const label = chip?.label ?? (state === "open" ? "Open" : state);
  const tone = chip?.tone ?? "neutral";
  return (
    <Chip tone={tone} className="text-2xs py-0 px-1.5">
      {label}
    </Chip>
  );
}
